import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Package, User, MapPin, Mail, Phone, CreditCard, Truck, ExternalLink } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useUserStore } from '@/lib/user-store';
import toast from 'react-hot-toast';
import type { OrderStatus } from '@/types';

/** Builds the printable address, skipping whatever is blank. State and the second line are
 *  recent additions, so older orders simply have fewer lines. */
function addressLines(a: {
  address?: string; address2?: string; city?: string; state?: string; country?: string; postalCode?: string;
}): string[] {
  const cityLine = [a.city, a.state, a.postalCode].filter(Boolean).join(', ');
  return [a.address, a.address2, cityLine, a.country].filter((x): x is string => Boolean(x && x.trim()));
}


const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500/10 text-yellow-500', confirmed: 'bg-blue-500/10 text-blue-500',
  processing: 'bg-purple-500/10 text-purple-500', shipped: 'bg-indigo-500/10 text-indigo-500',
  delivered: 'bg-green-500/10 text-green-500', cancelled: 'bg-red-500/10 text-red-500',
};
const STATUS_DOTS: Record<OrderStatus, string> = {
  pending: 'bg-yellow-500', confirmed: 'bg-blue-500', processing: 'bg-purple-500',
  shipped: 'bg-indigo-500', delivered: 'bg-green-500', cancelled: 'bg-red-500',
};
const ALL_STATUSES: OrderStatus[] = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

const AdminOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const orders = useAdminStore((s) => s.orders);
  const updateOrderStatus = useAdminStore((s) => s.updateOrderStatus);
  const updateOrderTracking = useAdminStore((s) => s.updateOrderTracking);
  const order = orders.find((o) => o._id === id);
  const [newStatus, setNewStatus] = useState<OrderStatus>(order?.status ?? 'pending');
  const [trackingId, setTrackingId] = useState(order?.trackingId ?? '');
  const [trackingUrl, setTrackingUrl] = useState(order?.trackingUrl ?? '');

  if (!order) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/admin/orders" className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all"><ArrowLeft size={16} /></Link>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Order Not Found</h1>
        </div>
        <div className="text-center py-16"><p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">This order does not exist</p></div>
      </div>
    );
  }

  const orderLabel = order.orderNumber || ('#' + order._id.slice(0, 8));

  const formatCurrency = (amount: number): string => {
    if (order.currency === 'GEL') return (amount / 100).toLocaleString() + ' GEL';
    return '$' + (amount / 100).toLocaleString();
  };
  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const notifyUser = (type: 'tracking_added' | 'status_changed', message: { en: string; ka: string }): void => {
    if (!order.userId) return;
    useUserStore.getState().addNotification(order.userId, {
      type,
      orderId: order._id,
      orderLabel,
      message,
    });
  };

  const handleStatusSave = (): void => {
    if (newStatus === order.status) { toast('Status unchanged'); return; }
    updateOrderStatus(order._id, newStatus);
    notifyUser('status_changed', {
      en: `Order ${orderLabel} status changed to ${newStatus}`,
      ka: `შეკვეთის ${orderLabel} სტატუსი შეიცვალა: ${newStatus}`,
    });
    toast.success('Status updated to ' + newStatus);
  };

  const handleTrackingSave = (): void => {
    if (!trackingId.trim()) { toast.error('Tracking ID is required'); return; }
    updateOrderTracking(order._id, trackingId.trim(), trackingUrl.trim());
    notifyUser('tracking_added', {
      en: `Tracking info added to order ${orderLabel}`,
      ka: `ტრეკინგ ინფორმაცია დაემატა შეკვეთას ${orderLabel}`,
    });
    toast.success('Tracking info saved');
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/orders" className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all"><ArrowLeft size={16} /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">Order Detail</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20 font-mono">{order._id}</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${STATUS_COLORS[order.status]}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOTS[order.status]}`} />
          {order.status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><User size={14} className="text-black/30 dark:text-white/30" /><h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">Customer</h3></div>
          <div className="space-y-3">
            <p className="text-[13px] font-bold">{order.customer.firstName} {order.customer.lastName}</p>
            <div className="flex items-center gap-2 text-[11px] text-black/50 dark:text-white/50"><Mail size={11} className="shrink-0" /><span className="truncate">{order.customer.email}</span></div>
            <div className="flex items-center gap-2 text-[11px] text-black/50 dark:text-white/50"><Phone size={11} className="shrink-0" /><span>{order.customer.phone}</span></div>
            <div className="flex items-start gap-2 text-[11px] text-black/50 dark:text-white/50">
              <MapPin size={11} className="shrink-0 mt-0.5" />
              <div>
                {order.deliveryMethod === 'pickup' && <p className="font-semibold">Pickup from gallery</p>}
                {order.shippingService && <p className="font-semibold mb-2">Georgian Post: {order.shippingService.serviceName} · Service {order.shippingService.parcelTypeId}</p>}
                {/* Checkout writes the delivery address to shippingAddress; the customer block
                    is only filled on older orders, so it is the fallback rather than the source. */}
                {addressLines(order.shippingAddress ?? order.customer).map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
                {addressLines(order.shippingAddress ?? order.customer).length === 0 && (
                  <p className="italic text-black/30 dark:text-white/30">No address on file</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="lg:col-span-2 bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><Package size={14} className="text-black/30 dark:text-white/30" /><h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">Items ({order.items.length})</h3></div>
          <div className="space-y-3">
            {order.items.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-black/[0.01] dark:bg-white/[0.01]">
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-black/[0.02] dark:bg-white/[0.02] shrink-0"><img src={item.artworkImage} alt={item.artworkTitle.en} className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-bold truncate">{item.artworkTitle.en}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/[0.04] dark:bg-white/[0.06] text-black/40 dark:text-white/40">{item.frame}{item.variantLabel ? ` · ${item.variantLabel.en || item.variantLabel.ka}` : ''}</span>
                    <span className="text-[10px] text-black/30 dark:text-white/30">Qty: {item.quantity}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] font-bold">{formatCurrency(item.totalPrice)}</p>
                  <p className="text-[9px] text-black/30 dark:text-white/30">{formatCurrency(item.unitPrice)} each</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><CreditCard size={14} className="text-black/30 dark:text-white/30" /><h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">Pricing</h3></div>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between"><span className="text-[11px] text-black/50 dark:text-white/50">Subtotal</span><span className="text-[11px] font-medium">{formatCurrency(order.subtotal)}</span></div>
            <div className="flex items-center justify-between"><span className="text-[11px] text-black/50 dark:text-white/50">Shipping</span><span className="text-[11px] font-medium">{order.shipping === 0 ? 'Free' : formatCurrency(order.shipping)}</span></div>
            <div className="flex items-center justify-between"><span className="text-[11px] text-black/50 dark:text-white/50">Tax</span><span className="text-[11px] font-medium">{formatCurrency(order.tax)}</span></div>
            <div className="border-t border-black/[0.06] dark:border-white/[0.06] pt-2.5 mt-2.5">
              <div className="flex items-center justify-between"><span className="text-[12px] font-bold">Total</span><span className="text-[14px] font-bold">{formatCurrency(order.total)}</span></div>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5">
          <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2">Update Status</label>
          <select value={newStatus} onChange={(e) => setNewStatus(e.target.value as OrderStatus)} className="w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all appearance-none cursor-pointer">
            {ALL_STATUSES.map((s) => (<option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>))}
          </select>
          <button onClick={handleStatusSave} className="mt-3 w-full flex items-center justify-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
            <Save size={14} /> Save Status
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><Truck size={14} className="text-black/30 dark:text-white/30" /><h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40">Tracking Info</h3></div>
          <div className="space-y-3">
            <div>
              <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1.5">Tracking ID</label>
              <input
                type="text"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                placeholder="e.g. GE1234567890"
                className="w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
              />
            </div>
            <div>
              <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-1.5">Tracking URL</label>
              <input
                type="url"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                placeholder="https://tracking.example.com/..."
                className="w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20"
              />
            </div>
            {order.trackingId && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
                <Truck size={12} className="text-indigo-500 shrink-0" />
                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 font-mono">{order.trackingId}</span>
                {order.trackingUrl && (
                  <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" className="ml-auto text-indigo-500 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors">
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
            )}
          </div>
          <button onClick={handleTrackingSave} className="mt-3 w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:bg-indigo-700 transition-colors">
            <Save size={14} /> Save Tracking
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-5">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-4">Timeline</h3>
          <div className="space-y-3">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Created</p>
              <p className="text-[11px] font-medium mt-0.5">{formatDate(order.createdAt)}</p>
            </div>
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30">Last Updated</p>
              <p className="text-[11px] font-medium mt-0.5">{formatDate(order.updatedAt)}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminOrderDetailPage;
